import React, { useState, useEffect } from 'react';
import { CheckCircle2, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown, AlertCircle } from 'lucide-react';
import { API, authFetch } from '../lib/api';

export default function LicenseGenerator() {
  const [loading, setLoading]               = useState(false);
  const [result, setResult]                 = useState(null);
  const [clients, setClients]               = useState([]);
  const [licenses, setLicenses]             = useState([]);
  const [pricing, setPricing]               = useState([]);
  const [amountAutoFilled, setAmountAutoFilled] = useState(false);

  const [form, setForm] = useState({
    clientId: '', clientName: '',
    tier: 'Enterprise',
    duration: '1 Year - Auto Renew',
    numberOfUsers: 1, numberOfSystems: 1,
    numberOfBranches: '', numberOfStores: '', numberOfWarehouses: '',
    hasSupport: false, supportType: '', amount: ''
  });

  useEffect(() => { loadData(); }, []);

  // Auto-fill amount from pricing
  useEffect(() => {
    if (!pricing.length) return;
    const tier = pricing.find(p => p.LicenseTier === form.tier);
    if (!tier) return;
    const base = Number(tier.BasePrice) || 0;
    const perUser = Number(tier.PricePerUser) || 0;
    const perSys  = Number(tier.PricePerSystem) || 0;
    const support = Number(tier.SupportPrice) || 0;
    const supportPct = Number(tier.SupportPercentage) || 0;
    const users   = Math.max(0, Number(form.numberOfUsers) || 0);
    const systems = Math.max(0, Number(form.numberOfSystems) || 0);
    let computed = base + (perUser * users) + (perSys * systems);
    if (form.hasSupport) computed += support + ((computed * supportPct) / 100);
    setForm(prev => {
      const currentAmount = Number(prev.amount);
      const shouldOverride = amountAutoFilled || !prev.amount || isNaN(currentAmount);
      if (!shouldOverride) return prev;
      return { ...prev, amount: Math.round(computed) };
    });
    setAmountAutoFilled(true);
    // eslint-disable-next-line
  }, [form.tier, form.numberOfUsers, form.numberOfSystems, form.hasSupport, pricing]);

  const loadData = async () => {
    try {
      const [cRes, lRes, pRes] = await Promise.all([
        authFetch(`${API}/license/clients`),
        authFetch(`${API}/license/all`),
        authFetch(`${API}/license/pricing`)
      ]);
      const [cD, lD, pD] = await Promise.all([cRes.json(), lRes.json(), pRes.json()]);
      setClients(cD);
      setLicenses(lD);
      setPricing(Array.isArray(pD) ? pD : []);
    } catch (err) { console.error('Failed to load data:', err); }
  };

  const getActiveLicense = (clientId) =>
    licenses.find(l => l.clientId === clientId && (!l.status || l.status === 'Active'));

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleClientSelect = (e) => {
    const clientId = e.target.value;
    const client = clients.find(c => c.clientId === clientId);
    if (client) setForm(p => ({ ...p, clientId: client.clientId, clientName: client.clientName }));
  };

  // ── New license (first-time) ─────────────────────────────────────────────
  const generate = async () => {
    if (!form.clientId || !form.clientName) { alert('Please select a client'); return; }
    setLoading(true); setResult(null);
    try {
      const res  = await authFetch(`${API}/license/generate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (data.success) { setResult({ ...data, _mode: 'new' }); loadData(); }
      else alert('License generation failed: ' + (data.error || data.detail));
    } catch { alert('Encryption Engine Error: Failed to sign license.'); }
    finally { setLoading(false); }
  };

  // ── Upgrade / Downgrade ──────────────────────────────────────────────────
  const upgradeDowngrade = async () => {
    if (!form.clientId || !form.tier) { alert('Select client and new tier'); return; }
    setLoading(true); setResult(null);
    try {
      const res  = await authFetch(`${API}/license/upgrade-downgrade/${form.clientId}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, newTier: form.tier })
      });
      const data = await res.json();
      if (data.success) { setResult({ ...data, _mode: 'upgrade' }); loadData(); }
      else alert('Upgrade/Downgrade failed: ' + (data.error || data.detail));
    } catch { alert('Failed to process upgrade/downgrade.'); }
    finally { setLoading(false); }
  };

  const activeLicense  = form.clientId ? getActiveLicense(form.clientId) : null;
  const hasLicense     = !!activeLicense;
  const currentTier    = activeLicense?.tier || activeLicense?.licenseTier || '';

  // Determine direction label
  const tierOrder = pricing.map(p => p.LicenseTier);
  const getDirection = (from, to) => {
    const fi = tierOrder.indexOf(from), ti = tierOrder.indexOf(to);
    if (fi < 0 || ti < 0 || fi === ti) return null;
    return ti > fi ? 'UPGRADE' : 'DOWNGRADE';
  };
  const direction = hasLicense && currentTier ? getDirection(currentTier, form.tier) : null;

  const formFields = (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
      {/* Tier selector — always visible */}
      <div>
        <label className="form-label">
          {hasLicense ? 'Change to Tier' : 'License Tier'}
          {hasLicense && direction && (
            <span style={{
              marginLeft: 8, fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
              background: direction === 'UPGRADE' ? '#dbeafe' : '#fef3c7',
              color: direction === 'UPGRADE' ? '#1d4ed8' : '#92400e'
            }}>
              {direction === 'UPGRADE' ? '↑ UPGRADE' : '↓ DOWNGRADE'}
            </span>
          )}
          {hasLicense && !direction && form.tier === currentTier && (
            <span style={{ marginLeft: 8, fontSize: 11, color: '#94a3b8' }}>same tier</span>
          )}
        </label>
        <select className="form-select" value={form.tier} onChange={e => f('tier', e.target.value)} data-testid="license-tier">
          {pricing.length > 0
            ? pricing.map(p => (
                <option key={p.LicenseTier} value={p.LicenseTier}>
                  {p.LicenseTier} (₹ {Number(p.BasePrice).toLocaleString()})
                </option>
              ))
            : <>
                <option>Enterprise</option>
                <option>Standard</option>
                <option>Lite</option>
              </>
          }
        </select>
        {pricing.find(p => p.LicenseTier === form.tier) && (
          <div style={{ fontSize: 12, color: '#3b82f6', marginTop: 4, fontWeight: 700 }}>
            Base: ₹ {Number(pricing.find(p => p.LicenseTier === form.tier).BasePrice).toLocaleString()} ·
            /user ₹ {Number(pricing.find(p => p.LicenseTier === form.tier).PricePerUser || 0).toLocaleString()} ·
            /system ₹ {Number(pricing.find(p => p.LicenseTier === form.tier).PricePerSystem || 0).toLocaleString()}
          </div>
        )}
      </div>

      <div>
        <label className="form-label">Number of Additional Users</label>
        <input className="form-input" type="number" min="0" value={form.numberOfUsers}
          onChange={e => f('numberOfUsers', parseInt(e.target.value) || 0)} placeholder="0" />
        <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>+3 default users (Vendor, Client, Support) = Total: {(form.numberOfUsers || 0) + 3}</div>
      </div>
      <div>
        <label className="form-label">Number of Systems</label>
        <input className="form-input" type="number" min="1" value={form.numberOfSystems}
          onChange={e => f('numberOfSystems', parseInt(e.target.value) || 1)} placeholder="1" />
      </div>

      <div style={{ gridColumn: '1 / -1', borderTop: '1px solid #e2e8f0', paddingTop: 12, marginTop: 4 }}>
        <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 10 }}>
          Business Unit Limits (optional — leave blank for unlimited)
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
          {[['numberOfBranches', 'Max Branches', 'license-max-branches'],
            ['numberOfStores',   'Max Stores',   'license-max-stores'],
            ['numberOfWarehouses','Max Warehouses','license-max-warehouses']].map(([k, lbl, tid]) => (
            <div key={k}>
              <label className="form-label">{lbl}</label>
              <input className="form-input" type="number" min="0"
                value={form[k]} onChange={e => f(k, e.target.value)}
                placeholder="Unlimited" data-testid={tid} />
            </div>
          ))}
        </div>
      </div>

      <div>
        <label className="form-label">Duration</label>
        <select className="form-select" value={form.duration} onChange={e => f('duration', e.target.value)} data-testid="license-duration">
          <option>1 Year - Auto Renew</option>
          <option>3 Years - Prepaid</option>
          <option>Perpetual License</option>
        </select>
      </div>
      <div>
        <label className="form-label">Amount (INR) <span style={{ color: '#3b82f6', fontSize: 12 }}>(auto-filled)</span></label>
        <input className="form-input" type="number" value={form.amount}
          onChange={e => { setAmountAutoFilled(false); f('amount', e.target.value); }}
          placeholder="₹ 0.00" data-testid="license-amount" />
        {amountAutoFilled && pricing.length > 0 && (
          <div style={{ fontSize: 12, color: '#10b981', marginTop: 4, fontWeight: 700 }}>✓ Auto-computed — you can override manually</div>
        )}
      </div>
      <div>
        <label className="form-label">Support</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#94a3b8', cursor: 'pointer' }}>
            <input type="checkbox" checked={form.hasSupport} onChange={e => f('hasSupport', e.target.checked)} />
            Include Support
          </label>
        </div>
      </div>
      {form.hasSupport && (
        <div>
          <label className="form-label">Support Type</label>
          <select className="form-select" value={form.supportType} onChange={e => f('supportType', e.target.value)}>
            <option value="">Select Type</option>
            <option>Premium</option>
            <option>Standard</option>
            <option>Basic</option>
          </select>
        </div>
      )}
    </div>
  );

  return (
    <div className="card">
      <div className="card-header">
        <span className="card-title">License Generator Console</span>
        <span className="card-badge">RSA-4096 / DNA-SIGNED</span>
      </div>

      <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {/* Client selector */}
        <div>
          <label className="form-label">Select Client</label>
          <select
            className={`form-select ${!form.clientId ? 'warn' : ''}`}
            value={form.clientId}
            onChange={handleClientSelect}
            data-testid="client-select"
          >
            <option value="">-- Select Client --</option>
            {clients.map(c => (
              <option key={c.clientId} value={c.clientId}>
                {c.clientName} ({c.clientId}) {getActiveLicense(c.clientId) ? '✓ Licensed' : ''}
              </option>
            ))}
          </select>
          {!form.clientId && (
            <div style={{ fontSize: 12, color: '#d97706', marginTop: 4, fontWeight: 700 }}>Required for DNA anchoring</div>
          )}
        </div>

        {/* Active license banner */}
        {hasLicense && (
          <div data-testid="license-current-banner" style={{
            padding: '10px 14px', background: '#f0f9ff',
            border: '1px solid #bae6fd', borderRadius: 8,
            display: 'flex', alignItems: 'center', gap: 10
          }}>
            <CheckCircle2 size={15} color="#0369a1" />
            <span style={{ color: '#0369a1', fontSize: 13, fontWeight: 600 }}>
              Active license: <strong>{currentTier}</strong>
              {activeLicense?.expiryDate
                ? ` · Expires ${new Date(activeLicense.expiryDate).toLocaleDateString()}`
                : ''}
            </span>
            <span style={{ marginLeft: 'auto', fontSize: 11, color: '#64748b' }}>
              Select a different tier below to upgrade or downgrade
            </span>
          </div>
        )}

        {formFields}

        {/* Result block */}
        {result && (
          <div className="result-block animate-fade-in" data-testid="license-result">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              {result._mode === 'upgrade'
                ? (result.direction === 'UPGRADE'
                    ? <ArrowUp size={14} color="#3b82f6" />
                    : <ArrowDown size={14} color="#f59e0b" />)
                : <CheckCircle2 size={14} color="#10b981" />}
              <span style={{ color: result._mode === 'upgrade' ? '#3b82f6' : '#10b981', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em' }}>
                {result._mode === 'upgrade'
                  ? `License ${result.direction}: ${result.prevTier} → ${result.newTier}`
                  : 'License Generated Successfully'}
              </span>
            </div>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, color: '#60a5fa', fontFamily: 'monospace', marginBottom: 4 }}>Product_ID:</div>
              <div style={{ background: '#1e293b', padding: '8px 12px', borderRadius: 6, fontFamily: 'monospace', fontSize: 12, color: '#93c5fd', fontWeight: 700 }}>
                {result.productId}
              </div>
            </div>
            <div style={{ fontSize: 12, color: '#60a5fa', fontFamily: 'monospace', marginBottom: 6 }}>License_Key (DNA-Signed):</div>
            <div style={{ background: '#1e293b', padding: '10px 14px', borderRadius: 6, fontFamily: 'monospace', fontSize: 12, color: '#93c5fd', wordBreak: 'break-all', fontWeight: 700 }}>
              {result.licenseKey}
            </div>
            {result.emailSent && (
              <div style={{ marginTop: 12, padding: 12, background: '#10b98120', border: '1px solid #10b981', borderRadius: 6 }}>
                <div style={{ fontSize: 12, color: '#10b981', fontWeight: 700, marginBottom: 4 }}>✓ Email Sent</div>
                <div style={{ fontSize: 12, color: '#064e3b' }}>License details sent to: {result.clientEmail}</div>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12, fontSize: 12, color: '#475569', fontWeight: 700, textTransform: 'uppercase' }}>
              <span>BLOCK: #{result.blockIndex}</span>
              <span>TXN: {result.transactionId}</span>
              <span>EXP: {result.expiryDate ? new Date(result.expiryDate).toLocaleDateString() : 'N/A'}</span>
            </div>

            {/* Org Structure Seeding Result */}
            {result.orgSeed && (
              <div style={{ marginTop: 14, padding: '10px 14px', borderRadius: 8, background: 'rgba(15,23,42,0.7)', border: '1px solid rgba(148,163,184,0.15)' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>
                  Org Structure Seeding
                </div>
                {result.orgSeed.reason && !result.orgSeed.seeded && (
                  <div style={{ fontSize: 11, color: '#475569', fontStyle: 'italic' }}>{result.orgSeed.reason}</div>
                )}
                {result.orgSeed.seeded && (
                  <>
                    {(result.orgSeed.branches || []).length > 0 && (
                      <div style={{ marginBottom: 4 }}>
                        <span style={{ fontSize: 10, color: '#64748b', marginRight: 6 }}>Branches:</span>
                        {result.orgSeed.branches.map(b => (
                          <span key={b.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, marginRight: 6, padding: '2px 6px', borderRadius: 4, background: b.status === 'seeded' ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)', color: b.status === 'seeded' ? '#10b981' : '#f87171' }}>
                            {b.status === 'seeded' ? '✓' : '✗'} {b.name}
                          </span>
                        ))}
                      </div>
                    )}
                    {(result.orgSeed.stores || []).length > 0 && (
                      <div style={{ marginBottom: 4 }}>
                        <span style={{ fontSize: 10, color: '#64748b', marginRight: 6 }}>Stores:</span>
                        {result.orgSeed.stores.map((s, i) => (
                          <span key={i} style={{ fontSize: 10, marginRight: 6, color: s.status === 'seeded' ? '#10b981' : s.status === 'not_found' ? '#64748b' : '#f87171' }}>
                            {s.status === 'seeded' ? `✓ ${s.name}` : s.status === 'not_found' ? 'StoreMaster not found in DB' : s.error}
                          </span>
                        ))}
                      </div>
                    )}
                    {(result.orgSeed.warehouses || []).length > 0 && (
                      <div>
                        <span style={{ fontSize: 10, color: '#64748b', marginRight: 6 }}>Warehouses:</span>
                        {result.orgSeed.warehouses.map((w, i) => (
                          <span key={i} style={{ fontSize: 10, marginRight: 6, color: w.status === 'seeded' ? '#10b981' : w.status === 'not_found' ? '#64748b' : '#f87171' }}>
                            {w.status === 'seeded' ? `✓ ${w.name}` : w.status === 'not_found' ? 'WarehouseMaster not found in DB' : w.error}
                          </span>
                        ))}
                      </div>
                    )}
                  </>
                )}
                {result.orgSeed.tenantDb && (
                  <div style={{ marginTop: 6, fontSize: 10, color: '#334155', fontFamily: 'monospace' }}>DB: {result.orgSeed.tenantDb}</div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Action button row */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10 }}>
          {hasLicense ? (
            <button
              className="btn btn-primary"
              onClick={upgradeDowngrade}
              disabled={loading || !form.clientId || form.tier === currentTier}
              data-testid="upgrade-downgrade-btn"
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            >
              {loading
                ? <><span className="spinner" style={{ width: 14, height: 14 }} /> Processing...</>
                : <><ArrowUpDown size={14} />
                    {direction === 'UPGRADE' ? 'Upgrade License' : direction === 'DOWNGRADE' ? 'Downgrade License' : 'Change License Tier'}
                    <ChevronRight size={13} />
                  </>}
            </button>
          ) : (
            <button
              className="btn btn-primary"
              onClick={generate}
              disabled={loading || !form.clientId}
              data-testid="generate-btn"
            >
              {loading
                ? <><span className="spinner" style={{ width: 14, height: 14 }} /> Generating...</>
                : <>Generate Secured License <ChevronRight size={13} /></>}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
